/* Where an element sits, for the layers Zana draws over the page (the
   takeover ring and cursor, the explain pins and notes). Boxes are measured
   against the layer itself, so they follow the page when it scrolls; the loop
   runs only while a layer is showing and only sets state when a box moves. */
import { useEffect, useRef, useState } from 'react';

export interface Box { left: number; top: number; width: number; height: number }

export const zSel = (key: string) => `.tc [data-z="${key.replace(/["\\]/g, '\\$&')}"]`;
export const zxSel = (key: string) => `.tc [data-zx="${key.replace(/["\\]/g, '\\$&')}"]`;

const same = (a: Box | null, b: Box | null) => (!a && !b) || (!!a && !!b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);

/** Boxes of `selectors`, relative to `layer`, kept current while `on`. */
export function useBoxes(layer: React.RefObject<HTMLElement>, selectors: string[], on: boolean): (Box | null)[] {
  const [boxes, setBoxes] = useState<(Box | null)[]>([]);
  const last = useRef<(Box | null)[]>([]);
  const key = selectors.join('|');
  useEffect(() => {
    if (!on) { last.current = []; setBoxes([]); return undefined; }
    let raf = 0;
    const tick = () => {
      const host = layer.current?.getBoundingClientRect();
      const next = selectors.map((s) => {
        const el = document.querySelector(s);
        if (!el || !host) return null;
        const r = el.getBoundingClientRect();
        if (!r.width && !r.height) return null;
        // Clip to every scrolling box it sits in (a wide table scrolls sideways).
        let l = r.left; let t = r.top; let rr = r.right; let b = r.bottom;
        for (let p = el.parentElement; p && !p.classList.contains('main'); p = p.parentElement) {
          const cs = getComputedStyle(p);
          if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
            const c = p.getBoundingClientRect();
            l = Math.max(l, c.left); t = Math.max(t, c.top); rr = Math.min(rr, c.right); b = Math.min(b, c.bottom);
          }
        }
        if (rr - l < 4 || b - t < 4) return null;
        return { left: l - host.left, top: t - host.top, width: rr - l, height: b - t };
      });
      if (next.length !== last.current.length || next.some((b, i) => !same(b, last.current[i]))) { last.current = next; setBoxes(next); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, on]);
  return boxes;
}

/** Resolves with the element once it is on the page, or null after `ms`. */
export function waitForEl(selector: string, ms: number, cancelled: () => boolean): Promise<Element | null> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const look = () => {
      if (cancelled()) { resolve(null); return; }
      const el = document.querySelector(selector);
      if (el) { resolve(el); return; }
      if (Date.now() - t0 > ms) { resolve(null); return; }
      setTimeout(look, 100);
    };
    look();
  });
}

export const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
